import { TFunction } from "i18next";
// PROJECT is the combined identifier for "PROJECT_FAMILY" and "PROJECT", but its one to multiple mapping so we can use project_id to get the unique project
// PHASE_BUILD is the combined identifier for "BUILD" and "PHASE"
//WARN: This map order is important, will fall from the largest Item unit to the smallest Item unit
export const IdentifierKeyMap = {
  PROJECTFAMILY_PROJECT_TESTLOCATION: "familyId_projectId_testLocationId",
  PROJECTFAMILY_PROJECT: "projectFamilyProject",
  // Combined Family Spec selector: one grouped dropdown (options grouped by Product Family,
  // each option a Sub-Category) that sets familyId + subcategoryId together. Like PHASE_BUILD,
  // it precedes its own two child keys so selecting it re-seeds them cleanly via the cascade.
  PROJECTFAMILY_PROJECTFAMILYSUBCATEGORY: "familyId_subcategoryId",
  // PROJECTFAMILY (familyId) MUST precede PROJECTFAMILYSUBCATEGORY (subcategoryId): family is the
  // parent, subcategory its child. The identifier cascade erases keys AFTER the selected level, so
  // if subcategory came first, selecting it would wipe the parent family selection.
  PROJECTFAMILY: "familyId",
  PROJECTFAMILYSUBCATEGORY: "subcategoryId",
  PROJECT: "projectId",
  TEST_LOCATION: "testLocationId",
  PHASE_BUILD: "phaseId_buildId",
  PHASE: "phaseId",
  BUILD: "buildId",
  TEST: "testId",
  CONFIG: "configId",
  SEQUENCE: "sequenceId",
  STATION: "checkCode",
  DSN: "dsn",
} as const;
export const getIdentifierNameMap = (t: TFunction) =>
  ({
    [IdentifierKeyMap.PROJECTFAMILY_PROJECT_TESTLOCATION]: t("identifiers.project"),
    [IdentifierKeyMap.PROJECTFAMILY_PROJECTFAMILYSUBCATEGORY]: t("identifiers.projectFamilySubcategory"),
    [IdentifierKeyMap.PROJECTFAMILYSUBCATEGORY]: t("identifiers.projectFamilySubcategory"),
    [IdentifierKeyMap.PROJECTFAMILY_PROJECT]: t("identifiers.projectFamilyProject"),
    [IdentifierKeyMap.PROJECTFAMILY]: t("identifiers.projectFamily"),
    [IdentifierKeyMap.PROJECT]: t("identifiers.project"),
    [IdentifierKeyMap.TEST_LOCATION]: t("identifiers.testLocation"),
    [IdentifierKeyMap.PHASE_BUILD]: t("identifiers.phaseBuild"),
    [IdentifierKeyMap.PHASE]: t("identifiers.phase"),
    [IdentifierKeyMap.BUILD]: t("identifiers.build"),
    [IdentifierKeyMap.TEST]: t("identifiers.test"),
    [IdentifierKeyMap.CONFIG]: t("identifiers.config"),
    [IdentifierKeyMap.SEQUENCE]: t("identifiers.sequence"),
    [IdentifierKeyMap.STATION]: t("identifiers.station"),
    [IdentifierKeyMap.DSN]: t("identifiers.dsn"),
  }) as const;

// Type representing the keys of IdentifierKeyMap (capitalized keys)
export type TIdentifierKey = keyof typeof IdentifierKeyMap;

// Type representing the values of IdentifierKeyMap (camelCase backend keys)
export type TIdentifierValue = (typeof IdentifierKeyMap)[TIdentifierKey];
export type TIdentifierObject = Partial<Record<TIdentifierValue, string>>;
export type TIdentifierMultiObject = Partial<Record<TIdentifierValue, string[]>>;

/**
 * Canonical test status keys — single source of truth for every status
 * vocabulary in the app. Each consumer (TestWaterfall, DataOverview,
 * ReadoutInput, status pills, color tokens, i18n) extracts the subset it
 * needs from this map.
 *
 * NOTE on NOT_STARTED vs TO_START vs QUEUED:
 *   - NOT_STARTED ("notStarted") is a broader legacy bucket meaning the test
 *     simply has not begun. TestWaterfall and the ReadoutInput status
 *     dropdown both use it.
 *   - TO_START ("toStart") and QUEUED ("queued") are DataOverview's finer
 *     split: assigned-and-ready vs waiting-in-line. DataOverview maps the
 *     legacy notStarted to either depending on context.
 *   - Both keys are intentionally kept so we don't have to refactor
 *     TestWaterfall today. New consumers should prefer the more specific
 *     TO_START / QUEUED when applicable, and fall back to NOT_STARTED only
 *     when the broader meaning is correct.
 */
export const TestStatusKeyMap = {
  PASS: "pass",
  FAIL: "fail",
  ONGOING: "ongoing",
  NOT_STARTED: "notStarted",
  TO_START: "toStart",
  QUEUED: "queued",
  CANCELLED: "cancelled",
  OBSERVATION: "observation",
  COMPLETED: "complete",
} as const;
export type TTestStatusKey = keyof typeof TestStatusKeyMap;
export type TTestStatusValue = (typeof TestStatusKeyMap)[TTestStatusKey];

/**
 * Display name map (mirrors getIdentifierNameMap).
 * i18n keys live under `testStatus.*` in src/locales/{en,zh}/common.json.
 */
export const getTestStatusNameMap = (t: TFunction) =>
  ({
    [TestStatusKeyMap.PASS]: t("testStatus.pass"),
    [TestStatusKeyMap.FAIL]: t("testStatus.fail"),
    [TestStatusKeyMap.ONGOING]: t("testStatus.ongoing"),
    [TestStatusKeyMap.NOT_STARTED]: t("testStatus.notStarted"),
    [TestStatusKeyMap.TO_START]: t("testStatus.toStart"),
    [TestStatusKeyMap.QUEUED]: t("testStatus.queued"),
    [TestStatusKeyMap.CANCELLED]: t("testStatus.cancelled"),
    [TestStatusKeyMap.OBSERVATION]: t("testStatus.observation"),
    [TestStatusKeyMap.COMPLETED]: t("testStatus.completed"),
  }) as const;

/**
 * Subset shown in the ReadoutInput header status dropdown. The wire type for
 * IReadoutTestData.testStatus is this same 4-tuple — ReadoutInput's BE only
 * emits one of these four lifecycle states.
 */
export const READOUT_TEST_STATUS_VALUES = [TestStatusKeyMap.CANCELLED, TestStatusKeyMap.NOT_STARTED, TestStatusKeyMap.ONGOING, TestStatusKeyMap.COMPLETED] as const;
export type TReadoutTestStatus = (typeof READOUT_TEST_STATUS_VALUES)[number];

/**
 * Backwards-compat alias for TestWaterfall and Color.ts. New code should
 * import TestStatusKeyMap directly.
 */
export const TestWaterfallStatus = {
  PASS: TestStatusKeyMap.PASS,
  FAIL: TestStatusKeyMap.FAIL,
  ONGOING: TestStatusKeyMap.ONGOING,
  NOT_STARTED: TestStatusKeyMap.NOT_STARTED,
  CANCELLED: TestStatusKeyMap.CANCELLED,
  OBSERVATION: TestStatusKeyMap.OBSERVATION,
} as const;
export type TTestWaterfallStatus = (typeof TestWaterfallStatus)[keyof typeof TestWaterfallStatus];
